import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Typography,
  useTheme,
} from '@/shared/ui/mui';
import { getRoleDisplay, getRoleLabelKey } from '@/app/routing/builders/nav';
import type { UserProfileProps } from '../types/index';

const isEmailLike = (value: string | null | undefined): boolean =>
  !!value && value.includes('@');

const UserProfile: FC<UserProfileProps> = ({ user, userType }) => {
  const theme = useTheme();
  const { t } = useTranslation('navigation');

  // Use provider pictureUrl first, fall back to Auth0 picture
  const avatarSrc = user?.pictureUrl || user?.picture || undefined;

  // Only show name if it's a real name, not the email repeated
  const displayName = user?.name && !isEmailLike(user.name) ? user.name : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Avatar
        src={avatarSrc}
        sx={{
          width: 40,
          height: 40,
          bgcolor: theme.palette.primary.main,
          fontSize: '0.95rem',
        }}
      >
        {displayName?.[0]?.toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        {displayName && (
          <Typography variant="body2" fontWeight={600} noWrap>
            {displayName}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" noWrap>
          {t(getRoleLabelKey(userType), { defaultValue: getRoleDisplay(userType) })}
        </Typography>
      </Box>
    </Box>
  );
};

export default UserProfile;
